/**
 * useResourceView — Resource Gantt View Hook
 *
 * Transforms the standard operation-centric Gantt data into
 * equipment-centric groupings for the resource Gantt view.
 *
 * Responsibilities:
 * 1. Load resource bindings (operation → equipment) from API
 * 2. Build Stage▸Equipment / pure Equipment group hierarchies
 * 3. Remap task.groupId to point to new equipment-based groups
 * 4. Render non-overlapping operations inline on equipment rows
 * 5. Split overlapping operations into conflict lanes and mark them
 */

import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import type { GanttGroup, GanttTask, YAxisMode } from '../wxb-ui/GanttChart/types';
import type { GanttNode } from '../ProcessTemplateGantt/types';
import { processTemplateV2Api } from '../../services';
import { parseScheduleIdFromTaskId } from './taskIds';
import {
  findGanttV5OverlappingIntervalIds,
  partitionGanttV5IntervalsIntoLayers,
} from '../wxb-ui/GanttV5/intervals';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface BindingRecord {
  template_schedule_id: number;
  resource_node_id: number;
  binding_mode: string;
  binding_role: string;
  node_name: string;
  node_class: string;
  equipment_system_type: string | null;
  equipment_class: string | null;
}

export interface EquipmentInfo {
  resourceNodeId: number;
  name: string;
  nodeClass: string;
  systemType: string | null;
  equipmentClass: string | null;
}

export interface UseResourceViewResult {
  /** Transformed groups (equipment-based) */
  resourceGroups: GanttGroup[];
  /** Tasks with remapped groupId + conflictType */
  resourceTasks: GanttTask[];
  /** Whether binding data is loading */
  loading: boolean;
  /** Force-refresh binding data (after a bind/unbind action) */
  refreshBindings: () => Promise<void>;
  /** Get the first storage-row equipment binding for a schedule ID. */
  getBindingForSchedule: (scheduleId: number) => EquipmentInfo | null;
  /** Get co-occupied AUXILIARY equipment for a schedule ID. */
  getCandidatesForSchedule: (scheduleId: number) => EquipmentInfo[];
}

// ---------------------------------------------------------------------------
// Stage color palette (extended for ≥6 stages)
// ---------------------------------------------------------------------------

// Data visualization palette follows the WXB theme so V5 Canvas rendering can
// resolve the same semantic colors without carrying hardcoded color literals.
const STAGE_COLOR_PALETTE = [
  'var(--wx-blue-700)', 'var(--wx-green-500)', 'var(--wx-cyan-500)',
  'var(--wx-amber-500)', 'var(--wx-red-500)', 'var(--wx-blue-500)',
  'var(--wx-green-600)', 'var(--wx-amber-700)', 'var(--wx-red-700)',
  'var(--wx-blue-800)',
];

// ---------------------------------------------------------------------------
// Shared O(n log n) overlap detection and lane assignment
// ---------------------------------------------------------------------------

/**
 * Split tasks into non-overlapping layers using a greedy algorithm.
 * Each layer contains tasks whose time ranges don't overlap.
 * Returns layers array — if length > 1, there's overlap.
 */
function splitIntoLayers(tasks: GanttTask[]): GanttTask[][] {
  return partitionGanttV5IntervalsIntoLayers(tasks);
}

/**
 * Return the task IDs whose time range overlaps at least one sibling task.
 *
 * The shared sorted sweep tracks only the interval with the furthest end. If a
 * later interval overlaps an earlier non-furthest interval, that earlier interval
 * was already marked when the furthest interval arrived. This avoids pair expansion.
 */
function findOverlappingTaskIds(tasks: GanttTask[]): Set<string> {
  return findGanttV5OverlappingIntervalIds(tasks);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract stage info from ganttNodes tree */
function extractStages(ganttNodes: GanttNode[]): Array<{
  id: string;
  name: string;
  code: string;
  order: number;
}> {
  const stages: Array<{ id: string; name: string; code: string; order: number }> = [];
  let order = 0;

  const traverse = (nodes: GanttNode[]) => {
    for (const node of nodes) {
      if (node.type === 'stage') {
        stages.push({
          id: node.id,
          name: node.title,
          code: node.stage_code || 'DEFAULT',
          order: order++,
        });
      }
      if (node.children) traverse(node.children);
    }
  };

  traverse(ganttNodes);
  return stages;
}

/**
 * Expand one operation task into one entry per co-occupied device (并占 / co-requisite).
 * The first storage-row device (index 0 of the schedule's device list) keeps the original task id.
 * Co-used devices get read-only visual mirrors with suffixed ids, but the mirrors stay
 * draggable so users can move the co-occupied operation from any occupied device row.
 * When >1 device, labels get a 并用×N marker.
 * Unbound schedules yield a single entry with equipKey 'unbound'.
 */
function expandTaskDevices(
  task: GanttTask,
  allDevicesMap: Map<number, EquipmentInfo[]>,
): Array<{ clone: GanttTask; equipKey: string; equipInfo: EquipmentInfo | null }> {
  const scheduleId = parseScheduleIdFromTaskId(task.id);
  const devices = scheduleId ? (allDevicesMap.get(scheduleId) ?? []) : [];
  if (devices.length === 0) {
    return [{ clone: task, equipKey: 'unbound', equipInfo: null }];
  }
  const coUse = devices.length > 1;
  return devices.map((equip, idx) => {
    const isPrimary = idx === 0;
    const clone: GanttTask = {
      ...task,
      id: isPrimary ? task.id : `${task.id}__equip-${equip.resourceNodeId}`,
      label: coUse ? `${task.label} ·并用×${devices.length}` : task.label,
      draggable: isPrimary ? task.draggable : task.draggable !== false,
      readOnly: isPrimary ? task.readOnly : true,
      deletable: isPrimary ? task.deletable : (task.deletable ?? task.readOnly !== true),
      hoverGroupId: coUse ? `co-occupied-operation-${scheduleId}` : task.hoverGroupId,
    };
    return { clone, equipKey: `equip-${equip.resourceNodeId}`, equipInfo: equip };
  });
}

// ---------------------------------------------------------------------------
// Group builders
// ---------------------------------------------------------------------------

function buildStageEquipmentGroups(
  stages: Array<{ id: string; name: string; code: string; order: number }>,
  tasks: GanttTask[],
  allDevicesMap: Map<number, EquipmentInfo[]>,
): { groups: GanttGroup[]; tasks: GanttTask[] } {
  const groups: GanttGroup[] = [];
  const outTasks: GanttTask[] = [];

  for (const stage of stages) {
    const stageColor = STAGE_COLOR_PALETTE[stage.order % STAGE_COLOR_PALETTE.length];

    // Stage header group
    groups.push({
      id: `res-stage-${stage.id}`,
      label: stage.name,
      color: stageColor,
      type: 'stage',
      showSummaryBar: true,
    });

    // Collect tasks for this stage
    const stageTasks = tasks.filter(t => {
      const stageId = t.data?.stageId;
      return stageId !== undefined && `stage_${stageId}` === stage.id;
    });

    // Group by equipment, expanding a co-occupied operation into one clone per device.
    const byEquipment = new Map<string, GanttTask[]>();
    const equipInfoByKey = new Map<string, EquipmentInfo | null>();

    for (const task of stageTasks) {
      for (const { clone, equipKey, equipInfo } of expandTaskDevices(task, allDevicesMap)) {
        const key = equipKey === 'unbound' ? `res-stage-${stage.id}-unbound` : equipKey;
        if (!byEquipment.has(key)) {
          byEquipment.set(key, []);
          equipInfoByKey.set(key, equipInfo);
        }
        byEquipment.get(key)!.push(clone);
      }
    }

    // Create equipment sub-groups
    for (const [equipGroupId, eqTasks] of Array.from(byEquipment.entries())) {
      const isUnbound = equipGroupId.endsWith('-unbound');
      const equipInfo = equipInfoByKey.get(equipGroupId) ?? null;

      const equipLabel = isUnbound
        ? '[未绑定] 设备'
        : (equipInfo?.name ?? equipGroupId);

      // Create the equipment group under the stage
      const fullEquipGroupId = `${stage.id}__${equipGroupId}`;
      groups.push({
        id: fullEquipGroupId,
        label: equipLabel,
        parentId: `res-stage-${stage.id}`,
        color: isUnbound ? 'var(--wx-fg-4)' : stageColor,
        type: 'equipment',
        showSummaryBar: true,
      });

      const layers = splitIntoLayers(eqTasks);
      const overlappingTaskIds = findOverlappingTaskIds(eqTasks);
      layers.forEach((layerTasks, index) => {
        const laneGroupId = `${fullEquipGroupId}__lane-${index + 1}`;
        groups.push({
          id: laneGroupId,
          label: `轨道 ${index + 1}`,
          parentId: fullEquipGroupId,
          color: isUnbound ? 'var(--wx-fg-4)' : stageColor,
          showSummaryBar: false,
          isSubRow: true,
        });

        for (const task of layerTasks) {
          outTasks.push({
            ...task,
            groupId: laneGroupId,
            conflictType: overlappingTaskIds.has(task.id) ? 'OVERLAP' : task.conflictType,
            renderOnGroupRow: true,
          });
        }
      });
    }
  }

  return { groups, tasks: outTasks };
}

function buildEquipmentGroups(
  tasks: GanttTask[],
  allDevicesMap: Map<number, EquipmentInfo[]>,
  stages: Array<{ id: string; name: string; code: string; order: number }>,
): { groups: GanttGroup[]; tasks: GanttTask[] } {
  const groups: GanttGroup[] = [];
  const outTasks: GanttTask[] = [];

  // Group all tasks by equipment (ignoring stage), expanding a co-occupied operation into
  // one clone per device so it appears on every device row it occupies.
  const byEquipment = new Map<string, GanttTask[]>();
  const equipInfoByKey = new Map<string, EquipmentInfo | null>();

  for (const task of tasks) {
    if (task.type === 'timeWindow') continue; // Skip time windows
    for (const { clone, equipKey, equipInfo } of expandTaskDevices(task, allDevicesMap)) {
      if (!byEquipment.has(equipKey)) {
        byEquipment.set(equipKey, []);
        equipInfoByKey.set(equipKey, equipInfo);
      }
      byEquipment.get(equipKey)!.push(clone);
    }
  }

  // Build stage color lookup for task coloring
  const stageColorMap = new Map<string, string>();
  for (const stage of stages) {
    stageColorMap.set(
      stage.id,
      STAGE_COLOR_PALETTE[stage.order % STAGE_COLOR_PALETTE.length],
    );
  }

  for (const [equipKey, eqTasks] of Array.from(byEquipment.entries())) {
    const isUnbound = equipKey === 'unbound';
    const equipInfo = equipInfoByKey.get(equipKey) ?? null;

    const equipLabel = isUnbound
      ? `[未绑定] 设备 (${eqTasks.length})`
      : (equipInfo?.name ?? equipKey);

    groups.push({
      id: `res-${equipKey}`,
      label: equipLabel,
      color: isUnbound ? 'var(--wx-fg-4)' : 'var(--wx-blue-800)',
      type: 'equipment',
      showSummaryBar: true,
    });

    const groupId = `res-${equipKey}`;
    const layers = splitIntoLayers(eqTasks);
    const overlappingTaskIds = findOverlappingTaskIds(eqTasks);

    layers.forEach((layerTasks, index) => {
      const laneGroupId = `${groupId}__lane-${index + 1}`;
      groups.push({
        id: laneGroupId,
        label: `轨道 ${index + 1}`,
        parentId: groupId,
        color: isUnbound ? 'var(--wx-fg-4)' : 'var(--wx-blue-800)',
        showSummaryBar: false,
        isSubRow: true,
      });

      for (const task of layerTasks) {
        // Color by stage
        const stageId = task.data?.stageId;
        const stageFullId = stageId !== undefined ? `stage_${stageId}` : undefined;
        const color = stageFullId ? stageColorMap.get(stageFullId) : undefined;

        outTasks.push({
          ...task,
          groupId: laneGroupId,
          conflictType: overlappingTaskIds.has(task.id) ? 'OVERLAP' : task.conflictType,
          color: color ?? task.color,
          renderOnGroupRow: true,
        });
      }
    });
  }

  return { groups, tasks: outTasks };
}

// ---------------------------------------------------------------------------
// Main Hook
// ---------------------------------------------------------------------------

export function useResourceView(
  templateId: number,
  ganttNodes: GanttNode[],
  tasks: GanttTask[],
  groups: GanttGroup[],
  yAxisMode: YAxisMode,
): UseResourceViewResult {
  // ---- Binding data ----
  const [bindings, setBindings] = useState<BindingRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const loadedTemplateRef = useRef<number>(0);

  // Load bindings once per template, regardless of Y-axis mode — the operation view's
  // right-click menu and the edit modal also rely on binding state.
  useEffect(() => {
    if (loadedTemplateRef.current === templateId) return; // already loaded

    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const data = await processTemplateV2Api.listBindingsByTemplate(templateId);
        if (!cancelled) {
          setBindings(data);
          loadedTemplateRef.current = templateId;
        }
      } catch (err) {
        console.error('Failed to load resource bindings:', err);
        if (!cancelled) setBindings([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => { cancelled = true; };
  }, [templateId, yAxisMode]);

  // ---- Derived maps ----
  // Multi-device occupancy: a single operation can own multiple binding rows. All bound devices
  // are equally required; PRIMARY/AUXILIARY only preserve the existing storage shape.
  //  - bindingMap (schedule → PRIMARY EquipmentInfo): first storage row — keeps the canonical task id,
  //    drives the right-click menu.
  //  - candidateMap (schedule → non-primary EquipmentInfo[]): the co-used devices.
  //  - allDevicesMap (schedule → [PRIMARY, ...co-used]): drives the multi-lane Gantt rendering
  //    (the operation block appears on every device's lane).
  const { bindingMap, candidateMap, allDevicesMap } = useMemo(() => {
    const bMap = new Map<number, EquipmentInfo>();
    const cMap = new Map<number, EquipmentInfo[]>();

    for (const b of bindings) {
      const info: EquipmentInfo = {
        resourceNodeId: b.resource_node_id,
        name: b.node_name,
        nodeClass: b.node_class,
        systemType: b.equipment_system_type,
        equipmentClass: b.equipment_class,
      };

      if (!b.binding_role || b.binding_role === 'PRIMARY') {
        // PRIMARY (or legacy rows without a role) → the first storage-row device.
        bMap.set(b.template_schedule_id, info);
      } else {
        // Co-used (并用必需) device — also occupied by this operation.
        const list = cMap.get(b.template_schedule_id) ?? [];
        list.push(info);
        cMap.set(b.template_schedule_id, list);
      }
    }

    // Build the full per-schedule device list, first storage-row device first.
    const allMap = new Map<number, EquipmentInfo[]>();
    const scheduleIds = new Set<number>([...Array.from(bMap.keys()), ...Array.from(cMap.keys())]);
    for (const sid of Array.from(scheduleIds)) {
      const list: EquipmentInfo[] = [];
      const primary = bMap.get(sid);
      if (primary) list.push(primary);
      const aux = cMap.get(sid);
      if (aux) list.push(...aux);
      allMap.set(sid, list);
    }

    return { bindingMap: bMap, candidateMap: cMap, allDevicesMap: allMap };
  }, [bindings]);

  // ---- Stage info ----
  const stages = useMemo(() => extractStages(ganttNodes), [ganttNodes]);

  // ---- Only operation tasks (exclude timeWindow) ----
  const opTasks = useMemo(
    () => tasks.filter(t => t.type !== 'timeWindow'),
    [tasks],
  );

  // ---- Build resource groups + tasks ----
  const { resourceGroups, resourceTasks } = useMemo(() => {
    if (yAxisMode === 'operation') {
      return { resourceGroups: groups, resourceTasks: tasks };
    }

    // Equipment views expand each operation into one task clone per co-occupied device, so
    // they return the fully-formed task list (lane groupIds already applied) directly.
    const result = yAxisMode === 'stage-equipment'
      ? buildStageEquipmentGroups(stages, opTasks, allDevicesMap)
      : buildEquipmentGroups(opTasks, allDevicesMap, stages);

    return {
      resourceGroups: result.groups,
      resourceTasks: result.tasks,
    };
  }, [yAxisMode, tasks, opTasks, groups, stages, allDevicesMap]);

  // ---- Refresh function (imperative, for binding UI) ----
  const refreshBindings = useCallback(async () => {
    setLoading(true);
    try {
      const data = await processTemplateV2Api.listBindingsByTemplate(templateId);
      setBindings(data);
      loadedTemplateRef.current = templateId;
    } catch (err) {
      console.error('Failed to refresh resource bindings:', err);
    } finally {
      setLoading(false);
    }
  }, [templateId]);

  // ---- Lookup for menu rendering ----
  const getBindingForSchedule = useCallback(
    (scheduleId: number): EquipmentInfo | null => {
      return bindingMap.get(scheduleId) ?? null;
    },
    [bindingMap],
  );

  // ---- Lookup for co-occupied equipment ----
  const getCandidatesForSchedule = useCallback(
    (scheduleId: number): EquipmentInfo[] => {
      return candidateMap.get(scheduleId) ?? [];
    },
    [candidateMap],
  );

  return {
    resourceGroups,
    resourceTasks,
    loading,
    refreshBindings,
    getBindingForSchedule,
    getCandidatesForSchedule,
  };
}
